import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { FinancialMonth } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, PlnDeltaTooltip, formatAxis, orderedLegendContent } from './chart-utils';

interface RevenueCostChartProps {
  months: FinancialMonth[];
  year: number;
}

export function RevenueCostChart({ months, year }: RevenueCostChartProps) {
  const chartData = months.map((m) => ({
    month: MONTH_LABELS[m.month - 1],
    revenue: m.revenueNet,
    cost: m.costNet,
    margin: m.marginNet,
  }));

  const config: ChartConfig = {
    revenue: { label: t('dashboard.financial.revenue'), color: 'var(--chart-1)' },
    cost: { label: t('dashboard.financial.costs'), color: 'var(--chart-4)' },
  };

  return (
    <ChartCard title={t('dashboard.financial.revenueVsCosts')}>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={chartData} barGap={2}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip
            content={
              <PlnDeltaTooltip
                config={config}
                year={year}
                deltaLabel={t('dashboard.financial.marginDelta')}
                deltaKey="margin"
              />
            }
          />
          <ChartLegend content={orderedLegendContent(['revenue', 'cost'])} />
          <Bar dataKey="revenue" fill="var(--color-revenue)" radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Bar dataKey="cost" fill="var(--color-cost)" radius={[4, 4, 0, 0]} maxBarSize={24} />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
